(() => {
  'use strict';
  function enhance(root) {
    if (root.dataset.goldConversionReady) return;
    root.dataset.goldConversionReady = 'true';
    const cleanups = [];
    const form = root.querySelector('#gp-request');

    // Retain the existing submission client and its description field.
    // Item choices are combined only when that client constructs FormData.
    const choices = root.querySelector('[data-gold-kinds]');
    const description = form && form.elements.description;
    if (choices && description && 'FormDataEvent' in window) {
      const selectedKinds = () => Array.from(choices.querySelectorAll('[data-gold-kind]:checked')).map(input => input.value);
      choices.hidden = false;
      const label = root.querySelector('[data-gold-description-label]');
      if (label) label.textContent = label.dataset.enhancedLabel;
      description.placeholder = description.dataset.enhancedPlaceholder;
      description.setAttribute('aria-describedby', 'gold-kinds-help');
      const updateRequired = () => { description.required = selectedKinds().length === 0; };
      choices.addEventListener('change', updateRequired);
      form.addEventListener('reset', () => setTimeout(updateRequired, 0));
      form.addEventListener('formdata', event => {
        const selected = selectedKinds();
        if (!selected.length) return;
        const notes = String(event.formData.get('description') || '').trim();
        event.formData.set('description', 'Items: ' + selected.join(', ') + (notes ? '\nNotes: ' + notes : ''));
      });
      updateRequired();
    }

    // Read the current rating from the merchant's installed review app.
    // Keep a plain review link if the app is delayed or its markup changes.
    const summaries = root.querySelectorAll('[data-review-summary]');
    const observedReviewRoots = new Set();
    function renderReviewSummary() {
      let scoreText = '', countText = '';
      const badge = document.getElementById('carthike-review-badge-container')?.shadowRoot;
      const grid = document.getElementById('carthike-grid-div')?.shadowRoot;
      for (const source of [badge, grid]) {
        if (!source) continue;
        if (!observedReviewRoots.has(source)) {
          observedReviewRoots.add(source);
          const observer = new MutationObserver(renderReviewSummary);
          observer.observe(source, {childList: true, subtree: true, characterData: true});
          cleanups.push(() => observer.disconnect());
        }
        const rating = source === badge
          ? source.querySelector('.badge-rating-stars')
          : source.querySelector('h2')?.parentElement.querySelector('span');
        const container = source === badge
          ? source.querySelector('.carthike-review-badge-wrapper')
          : source.querySelector('h2')?.parentElement;
        const score = rating?.textContent.trim() || '';
        const count = container?.textContent.match(/\b([\d,]+)\s+Reviews?\b/i);
        if (/^[0-5](?:\.\d)?$/.test(score) && Number(score) > 0 && Number(score) <= 5 && count && Number(count[1].replace(/,/g, '')) > 0) {
          scoreText = score;
          countText = count[1];
          break;
        }
      }
      summaries.forEach(link => {
        const available = !!scoreText;
        link.querySelector('[data-review-value]').hidden = !available;
        link.querySelector('[data-review-fallback]').hidden = available;
        if (available) {
          link.querySelector('[data-review-score]').textContent = scoreText;
          link.querySelector('[data-review-count]').textContent = countText;
        }
      });
    }
    renderReviewSummary();
    let reviewAttempts = 0;
    const reviewTimer = setInterval(() => {
      renderReviewSummary();
      if (++reviewAttempts >= 60 || observedReviewRoots.size === 2) clearInterval(reviewTimer);
    }, 500);
    cleanups.push(() => clearInterval(reviewTimer));

    // Illustrative payout uses the visible, already-loaded rate table.
    // It never makes a new price request or changes the user's calculator.
    const example = root.querySelector('[data-gold-example]');
    const ratePanel = root.querySelector('[data-rates-store]')?.parentElement;
    const money = cents => new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD'}).format(cents / 100);
    function updateExample() {
      if (!example || !ratePanel) return;
      const postal = !root.querySelector('[data-rates-post]').hidden;
      const rows = root.querySelector(postal ? '[data-post-rows]' : '[data-store-rows]');
      const row = Array.from(rows?.children || []).find(item => item.firstElementChild?.textContent.trim() === '18ct');
      const rateText = row?.lastElementChild?.textContent.trim() || '';
      const rate = /^\$[\d,]+\.\d{2}$/.test(rateText) ? Number(rateText.replace(/[$,]/g, '')) : NaN;
      const gross = Math.round(rate * 10 * 100);
      const net = gross;
      example.hidden = !Number.isFinite(rate) || rate <= 0 || net <= 0;
      if (example.hidden) return;
      example.querySelector('[data-example-rate]').textContent = rateText + ' / g';
      example.querySelector('[data-example-gross]').textContent = money(gross);
      example.querySelector('[data-example-net]').textContent = money(net);
    }
    if (ratePanel && example) {
      const observer = new MutationObserver(updateExample);
      observer.observe(ratePanel, {childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden']});
      cleanups.push(() => observer.disconnect());
      updateExample();
    }

    const sticky = root.querySelector('[data-gold-sticky]');
    const heroActions = root.querySelector('[class$="__hero"] [class$="__actions"]');
    if (sticky && heroActions && 'IntersectionObserver' in window) {
      const mobile = matchMedia('(max-width: 620px)');
      let formVisible = false, heroVisible = true;
      const updateSticky = () => {
        const editing = !!form?.contains(document.activeElement) && /INPUT|SELECT|TEXTAREA/.test(document.activeElement.tagName);
        const pastHero = heroActions.getBoundingClientRect().bottom < 0;
        const visible = mobile.matches && pastHero && !heroVisible && !formVisible && !editing;
        sticky.hidden = !visible;
        document.body.classList.toggle('ld-gold-cta-visible', visible);
      };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === heroActions) heroVisible = entry.isIntersecting;
          if (entry.target === form) formVisible = entry.isIntersecting;
        });
        updateSticky();
      });
      observer.observe(heroActions);
      if (form) observer.observe(form);
      mobile.addEventListener('change', updateSticky);
      form?.addEventListener('focusin', updateSticky);
      form?.addEventListener('focusout', updateSticky);
      cleanups.push(() => {
        observer.disconnect();
        mobile.removeEventListener('change', updateSticky);
        form?.removeEventListener('focusin', updateSticky);
        form?.removeEventListener('focusout', updateSticky);
        document.body.classList.remove('ld-gold-cta-visible');
      });
      updateSticky();
    }
    window.addEventListener('pagehide', () => cleanups.forEach(cleanup => cleanup()), {once: true});
  }
  document.querySelectorAll('[data-gold-channel]').forEach(enhance);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-gold-channel]').forEach(enhance);
  });
})();
